import Swal, { SweetAlertResult } from 'sweetalert2';

const defaultNotificationDuration: number = 5000;
const defaultErrorNotificationDuration: number = 20000;

export enum MessageUtilResultType {
    None = 0,
    Ok = 1,
    Cancel = 2,
    Close = 3
}

export class MessageUtilResult {
    public value: any;

    public constructor(public type: MessageUtilResultType = MessageUtilResultType.None) {
    }

    public get isOk(): boolean {
        return this.type === MessageUtilResultType.Ok;
    }

    public get isCancel(): boolean {
        return this.type === MessageUtilResultType.Cancel;
    }
}

export class MessageUtil {
    private _locale: string = '';

    constructor(private messages?: any, locale?: string) {
        this._locale = locale || 'el';
        this.messages = this.messages || {};
        this.getTranslation.bind(this);
    }

    public get locale() {
        return this._locale;
    }

    public getTranslation(phrase) {
        if (!phrase) return phrase;
        const res = this.messages[phrase] || phrase;
        return res;
    }

    private replaceProperties(targetObj, propertiesDict) {
        if (!targetObj || !propertiesDict)
            return;
        Object.keys(propertiesDict).forEach(p => {
            if (targetObj.hasOwnProperty(p)) {
                targetObj[propertiesDict[p]] = targetObj[p];
                delete targetObj[p];
            }
        });
    }

    private getTypeOptions(type) {
        if (!type) return {};
        const typesDict = {
            info: {
                icon: 'info',
                title: this.getTranslation('information'),
                // backgroundClass: 'bg-info',
                textClass: 'text-info',
                get fontIconClass() { return `icon-info fas fa-info-circle ${this.textClass}`; }
            },
            success: {
                icon: 'success',
                title: this.getTranslation('success'),
                // backgroundClass: 'bg-success',
                textClass: 'text-success',
                get fontIconClass() { return `icon-success fas fa-check-circle ${this.textClass}`; }
            },
            warning: {
                icon: 'warning',
                title: this.getTranslation('warning'),
                // backgroundClass: 'bg-warning',
                textClass: 'text-warning',
                get fontIconClass() {
                    return `icon-warning fas fa-info-circle ${this.textClass}`;
                }
            },
            error: {
                icon: 'error',
                title: this.getTranslation('error'),
                // backgroundClass: 'bg-danger',
                textClass: 'text-danger',
                get fontIconClass() {
                    return `icon-error fas fa-times-circle ${this.textClass}`;
                }
            },
            question: {
                icon: 'question',
                title: this.getTranslation('question'),
                // backgroundClass: 'bg-warning',
                textClass: 'text-warning',
                get fontIconClass() {
                    return `icon-question fas fa-question-circle ${this.textClass}`;
                }
            },
            confirm: {
                icon: 'warning',
                title: this.getTranslation('confirmation'),
                // backgroundClass: 'bg-warning',
                textClass: 'text-warning',
                get fontIconClass() {
                    return `icon-confirm fas fa-info-circle ${this.textClass}`;
                }
            },
            progress: {
                icon: '',
                title: this.getTranslation('progress'),
                // backgroundClass: 'bg-info',
                textClass: 'text-info',
                get fontIconClass() {
                    return `icon-progress fas fa-spinner fa-spin ${this.textClass}`;
                }
            }
        };
        const res = typesDict[type] || {};
        return res;
    }

    private getSwalOptionsFromOptions(options: any): any {
        if (!options)
            return null;
        const res: any = {};
        Object.assign(res, options);
        ['type', 'duration', 'multiple'].forEach(p => {
            if (res.hasOwnProperty(p)) {
                delete res[p];
            }
        });
        if (!res.showClass) {
            res.showClass = {
                popup: 'swal-popup-show'
            };
        }
        if (!res.hideClass) {
            res.hideClass = {
                popup: 'swal-popup-hide'
            };
        }
        if (options.type !== 'progress') {
            const cancelButtonType = 'secondary';
            let confirmButtonType = 'primary';
            switch (options.type) {
            case 'success':
            case 'warning':
            case 'danger':
                confirmButtonType = options.type;
                break;
            case 'error':
                confirmButtonType = 'danger';
                break;
            case 'info':
            case 'question':
            case 'prompt':
                confirmButtonType = 'info';
                break;
            case 'confirm':
                confirmButtonType = 'warning';
                break;
            }
            res.buttonsStyling = false;
            if (!res.customClass) {
                res.customClass = {
                    /* title: `text-${confirmButtonType}`, */
                    confirmButton: `btn btn-${confirmButtonType} mx-2`,
                    cancelButton: `btn btn-${cancelButtonType} mx-2`
                };
            }
        }
        return res;
    }

    private getMessageUtilResultFromSwalResult(swalResult: SweetAlertResult) {
        const res: MessageUtilResult = new MessageUtilResult();
        if (swalResult) {
            res.value = swalResult.value;
            if (swalResult.dismiss) {
                switch (swalResult.dismiss) {
                case Swal.DismissReason.cancel:
                    res.type = MessageUtilResultType.Cancel;
                    break;
                default:
                    res.type = MessageUtilResultType.Close;
                    break;
                }
            } else {
                res.type = MessageUtilResultType.Ok;
            }
        }
        return res;
    }

    public close() {
        Swal.close();
    }

    public async messageBox(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        const typeOptions = this.getTypeOptions(options.type);
        options.title = options.title || typeOptions.title;
        options.icon = options.icon || typeOptions.icon;
        options.confirmButtonText = options.confirmButtonText || this.getTranslation('ok');
        if (options.allowOutsideClick === undefined || options.allowOutsideClick === null)
            options.allowOutsideClick = false;
        if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
            options.allowEscapeKey = false;
        if (options.backdrop === undefined || options.backdrop === null)
            options.backdrop = true;
        options.heightAuto = false;
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }

    public async messageBoxInfo(options): Promise<MessageUtilResult> {
        options = options || {};
        options.type = 'info';
        return await this.messageBox(options);
    }

    public async messageBoxQuestion(options): Promise<MessageUtilResult> {
        options = options || {};
        options.type = 'question';
        return await this.messageBox(options);
    }

    public async messageBoxSuccess(options): Promise<MessageUtilResult> {
        options = options || {};
        options.type = 'success';
        return await this.messageBox(options);
    }

    public async messageBoxWarning(options): Promise<MessageUtilResult> {
        options = options || {};
        options.type = 'warning';
        return await this.messageBox(options);
    }

    public async messageBoxError(options): Promise<MessageUtilResult> {
        options = options || {};
        options.type = 'error';
        return await this.messageBox(options);
    }

    public async notify(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        if (options.toast === undefined || options.toast === null)
            options.toast = true;
        const typeOptions = this.getTypeOptions(options.type);
        options.icon = options.icon || typeOptions.icon;
        options.position = options.position || 'top-end';
        options.timer = options.timer || options.duration || defaultNotificationDuration;
        if (options.showConfirmButton === undefined || options.showConfirmButton === null)
            options.showConfirmButton = false;
        if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
            options.allowEscapeKey = false;
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }

    public async notifyInfo(options): Promise<MessageUtilResult> {
        options = options || {};
        options.duration = options.duration || defaultNotificationDuration;
        options.type = options.type || 'info';
        return this.notify(options);
    }

    public async notifyQuestion(options): Promise<MessageUtilResult> {
        options = options || {};
        options.duration = options.duration || defaultNotificationDuration;
        options.type = options.type || 'question';
        return this.notify(options);
    }

    public async notifySuccess(options): Promise<MessageUtilResult> {
        options = options || {};
        options.duration = options.duration || defaultNotificationDuration;
        options.type = options.type || 'success';
        return await this.notify(options);
    }

    public async notifyWarning(options): Promise<MessageUtilResult> {
        options = options || {};
        options.duration = options.duration || defaultErrorNotificationDuration;
        options.type = options.type || 'warning';
        return this.notify(options);
    }

    public async notifyError(options): Promise<MessageUtilResult> {
        options = options || {};
        options.duration = options.duration || defaultErrorNotificationDuration;
        options.type = options.type || 'error';
        return this.notify(options);
    }

    public async confirm(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        options.type = options.type || 'confirm';
        const typeOptions = this.getTypeOptions(options.type);
        options.title = options.title || typeOptions.title;
        options.icon = options.icon || typeOptions.icon;
        options.showCancelButton = true;
        if (options.reverseButtons === undefined || options.reverseButtons === null)
            options.reverseButtons = false;
        options.confirmButtonText = options.confirmButtonText || this.getTranslation('ok');
        options.cancelButtonText = options.cancelButtonText || this.getTranslation('cancel');
        if (options.allowOutsideClick === undefined || options.allowOutsideClick === null)
            options.allowOutsideClick = false;
        if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
            options.allowEscapeKey = false;
        if (options.backdrop === undefined || options.backdrop === null)
            options.backdrop = true;
        options.heightAuto = false;
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }

    public async confirmYesNo(options): Promise<MessageUtilResult> {
        options = options || {};
        options.confirmButtonText = options.confirmButtonText || this.getTranslation('yes');
        options.cancelButtonText = options.cancelButtonText || this.getTranslation('no');
        return await this.confirm(options);
    }

    public async prompt(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        const typeOptions = this.getTypeOptions(options.type);
        options.icon = options.icon || typeOptions.icon;
        options.showCancelButton = true;
        if (options.reverseButtons === undefined || options.reverseButtons === null)
            options.reverseButtons = false;
        options.confirmButtonText = options.confirmButtonText || this.getTranslation('ok');
        options.cancelButtonText = options.cancelButtonText || this.getTranslation('cancel');
        if (options.allowOutsideClick === undefined || options.allowOutsideClick === null)
            options.allowOutsideClick = false;
        if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
            options.allowEscapeKey = false;
        if (options.backdrop === undefined || options.backdrop === null)
            options.backdrop = true;
        options.heightAuto = false;
        options.input = 'text';
        options.inputValue = options.defaultValue;
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }

    public async selectFiles(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        const typeOptions = this.getTypeOptions(options.type);
        options.icon = options.icon || typeOptions.icon;
        options.showCancelButton = true;
        if (options.reverseButtons === undefined || options.reverseButtons === null)
            options.reverseButtons = false;
        options.confirmButtonText = options.confirmButtonText || this.getTranslation('ok');
        options.cancelButtonText = options.cancelButtonText || this.getTranslation('cancel');
        if (options.allowOutsideClick === undefined || options.allowOutsideClick === null)
            options.allowOutsideClick = false;
        if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
            options.allowEscapeKey = false;
        if (options.backdrop === undefined || options.backdrop === null)
            options.backdrop = true;
        options.heightAuto = false;
        const buttonSelectId = `buttonSelect`;
        const buttonClearId = `buttonClear`;
        const fileInputId = `fileInput`;
        const fileTextId = `fileText`;
        const htmlContent =
            `<div class="text-center ${typeOptions.textClass ? ' ' + typeOptions.textClass : ''}">
                ${typeOptions.fontIconClass? '<div class="message-icon"><i class="' + typeOptions.fontIconClass + '"></i></div>': ''}
                <div>${options.html}</div>
                <div id="style="margin-top: 8px; width: 100%;">
                    <input id="${fileInputId}" class="form-control" type="file" ${options.multiple ? 'multiple' : ''}
                        style="visibility: collapse; height: 0; width: 0; margin: 0; padding: 0;"
                        onchange="var elementFilesInput = document.getElementById('${fileInputId}');
                            var elementFilesText = document.getElementById('${fileTextId}');
                            var files = Array.from(elementFilesInput.files);
                            if (!files || files.length <= 0)
                                elementFilesText.value = '';
                            else
                                elementFilesText.value = files.map(f => f.name).join(', ');"
                    />
                    <table style="width: 100%;">
                        <tr>
                            <td style="height: 36px; width:40px; text-align: center;">
                                <button id="${buttonSelectId}" 
                                    onclick="event.preventDefault(); event.stopPropagation();
                                        var elementFilesInput = document.getElementById('${fileInputId}');
                                        setTimeout(() => elementFilesInput.click(), 0);"
                                    class="btn btn-success" style="height: 36px; width:40px;">
                                    <i class="fas fa-file-image"></i>
                                </button>
                            </td>
                            <td>
                                <textarea id="${fileTextId}"
                                    class="form-control" rows="${options.multiple ? 3 : 1.25}" readonly>
                                </textarea>
                            </td>
                            <td style="height: 36px; width:40px; text-align: center;">
                                <button id="${buttonClearId}" 
                                    onclick="event.preventDefault(); event.stopPropagation();
                                    var elementFilesInput = document.getElementById('${fileInputId}');
                                    var elementFilesText = document.getElementById('${fileTextId}');
                                    elementFilesInput.value = ''; elementFilesText.value = '';"
                                    class="btn btn-warning" style="height: 36px; width:40px;">
                                    <i class="fas fa-window-close"></i>
                                </button>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>`;
        options.html = htmlContent;
        /*
        options.input = 'file'
        options.inputAttributes = { 'multiple': true };
        */
        options.preConfirm = value => {
            let res: any = null;
            if (value) {
                const elementFilesInput: any = document.getElementById(fileInputId);
                if (elementFilesInput)
                    res = Array.from(elementFilesInput.files);
            }
            return res;
        };
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }

    public async progress(options): Promise<MessageUtilResult> {
        this.replaceProperties(options, { message: 'html' });
        options.type = options.type || 'progress';
        if (options.toast === undefined || options.toast === null) {
            options.toast = false;
        }
        if (options.toast) {
            options.position = options.position || 'top-end';
        } else {
            options.position = options.position || 'center';
        }
        const typeOptions = this.getTypeOptions(options.type);
        const title: string = options.title;
        //options.title = `<span class="fas fa-spinner fa-spin ${typeOptions.textClass}" style="font-size: 1.5em; padding: 4px 8px;"></span>`;
        options.title = `<span class="spinner-lg"></span>`;
        if (title) {
            if (!options.toast) {
                options.title += `<span style="margin: 8px 12px;">${title}</span>`;
            } else {
                options.title += `<span style="margin: 6px;">${title}</span>`;
            }
        }
        if (!options.toast) {
            if (options.allowOutsideClick === undefined || options.allowOutsideClick === null)
                options.allowOutsideClick = false;
            if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
                options.allowEscapeKey = true;
        } else {
            if (options.allowEscapeKey === undefined || options.allowEscapeKey === null)
                options.allowEscapeKey = false;
        }
        if (options.showConfirmButton === undefined || options.showConfirmButton === null)
            options.showConfirmButton = false;
        const swalResult = await Swal.fire(this.getSwalOptionsFromOptions(options));
        const result = this.getMessageUtilResultFromSwalResult(swalResult);
        return result;
    }
}

const messageUtilDict: any = {
};

export function getMessageUtil(messages?: any, locale?: string): MessageUtil {
    locale = locale || 'el';
    if (!messageUtilDict[locale]) {
        messageUtilDict[locale] = new MessageUtil(messages, locale);
    }
    return messageUtilDict[locale];
}
